import type { Annotation } from '../../../../../api/types';
import type { setupPlugins } from '../plugins';
import annotationStyles from './annotations.css?raw';
import {
  DEFAULT_RECT_SIZE,
  DEFAULT_TEXT_WIDTH,
  MIN_RECT_SIZE,
  colorFromId,
  createAnnotationId,
  pickAnnotationColor,
  toRgba,
} from './annotation-utils';

type Area = ReturnType<typeof setupPlugins>['area'];

type AnnotationLayerOptions = {
  area: Area;
  container: HTMLElement;
  onChange?: (annotations: Annotation[]) => void;
};

type DragState = {
  id: string;
  mode: 'move' | 'resize';
  offsetX: number;
  offsetY: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  originWidth: number;
  originHeight: number;
  startWidth: number;
  startHeight: number;
  nextX: number;
  nextY: number;
  nextWidth: number;
  nextHeight: number;
  heightEnabled: boolean;
};

type AnnotationElements = {
  root: HTMLDivElement;
  content?: HTMLDivElement;
  deleteButton: HTMLButtonElement;
  colorInput?: HTMLInputElement;
  resizeHandle: HTMLDivElement;
};

const ANNOTATION_CLIPBOARD_KIND = 'klarvos/annotation';

export function createAnnotationLayer({
  area,
  container,
  onChange,
}: AnnotationLayerOptions) {
  const rootNode = container.getRootNode();
  const styleHost =
    rootNode instanceof ShadowRoot ? rootNode : document.head;
  if (!styleHost.querySelector('style[data-annotation-layer]')) {
    const style = document.createElement('style');
    style.dataset.annotationLayer = 'true';
    style.textContent = annotationStyles;
    styleHost.appendChild(style);
  }

  const layer = document.createElement('div');
  layer.className = 'annotation-layer';
  area.area.content.add(layer);
  const holder = area.area.content.holder;
  const firstChild = holder.firstElementChild;
  if (firstChild && firstChild !== layer) {
    area.area.content.reorder(layer, firstChild).catch(() => {});
  }

  let annotations: Annotation[] = [];
  const elements = new Map<string, AnnotationElements>();
  let dragState: DragState | null = null;
  let selectedId: string | null = null;
  let lastPointer = { x: 0, y: 0 };
  let pendingEditId: string | null = null;

  const normalizeHexColor = (value: string): string | null => {
    const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
    if (!match) return null;
    const [, hex] = match;
    if (hex.length === 3) {
      return `#${hex
        .split('')
        .map((chunk) => chunk + chunk)
        .join('')
        .toLowerCase()}`;
    }
    return `#${hex.toLowerCase()}`;
  };

  const updatePointer = (e: PointerEvent | MouseEvent) => {
    area.area.setPointerFrom(e as PointerEvent);
    lastPointer = { ...area.area.pointer };
  };

  const handleContextMenu = (event: MouseEvent) => {
    updatePointer(event);
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const annotationRoot = target.closest('.annotation') as HTMLElement | null;
    const annotationId = annotationRoot?.dataset.annotationId;
    if (annotationId) {
      setSelection(annotationId);
    }
  };

  const handleBackgroundPointerDown = (event: PointerEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.annotation')) {
      clearSelection();
    }
  };

  const clearSelection = () => {
    if (!selectedId) return;
    const current = elements.get(selectedId);
    current?.root.classList.remove('selected');
    selectedId = null;
  };

  const setSelection = (id: string | null) => {
    if (selectedId === id) return;
    if (selectedId) {
      elements.get(selectedId)?.root.classList.remove('selected');
    }
    selectedId = id;
    if (id) {
      elements.get(id)?.root.classList.add('selected');
    }
  };

  const emitChange = () => {
    onChange?.([...annotations]);
  };

  const getAnnotation = (id: string) =>
    annotations.find((item) => item.id === id);

  const updateAnnotation = (id: string, next: Partial<Annotation>) => {
    const index = annotations.findIndex((item) => item.id === id);
    if (index === -1) return;
    annotations = annotations.map((item, idx) =>
      idx === index ? { ...item, ...next } : item
    );
    sync();
    emitChange();
  };

  const removeAnnotation = (id: string) => {
    annotations = annotations.filter((item) => item.id !== id);
    const el = elements.get(id);
    if (el) {
      layer.removeChild(el.root);
      elements.delete(id);
    }
    if (selectedId === id) {
      selectedId = null;
    }
    emitChange();
  };

  const startEditing = (id: string) => {
    const el = elements.get(id);
    if (!el?.content) return;
    const root = el.root;
    if (root.classList.contains('editing')) return;
    root.classList.add('editing');
    const content = el.content;
    const previous = content.textContent ?? '';
    content.contentEditable = 'true';
    content.focus();
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      const range = document.createRange();
      range.selectNodeContents(content);
      selection.addRange(range);
    }

    const finish = (commit: boolean) => {
      root.classList.remove('editing');
      content.contentEditable = 'false';
      content.removeEventListener('blur', onBlur);
      content.removeEventListener('keydown', onKeyDown);
      if (!commit) {
        content.textContent = previous;
        return;
      }
      const text = content.textContent ?? '';
      updateAnnotation(id, { text });
    };

    const onBlur = () => finish(true);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        content.blur();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        finish(false);
      }
    };

    content.addEventListener('blur', onBlur);
    content.addEventListener('keydown', onKeyDown);
  };

  const applyAnnotationStyles = (
    annotation: Annotation,
    el: AnnotationElements
  ) => {
    const { root } = el;
    root.dataset.type = annotation.type;
    root.style.left = `${annotation.x}px`;
    root.style.top = `${annotation.y}px`;

    const baseColor = annotation.color ?? colorFromId(annotation.id);
    root.style.setProperty('--annotation-color', baseColor);
    root.style.setProperty('--annotation-bg', toRgba(baseColor, 0.18));
    if (el.colorInput) {
      const pickerColor =
        normalizeHexColor(baseColor) ??
        normalizeHexColor(colorFromId(annotation.id)) ??
        '#fbbf24';
      if (el.colorInput.value !== pickerColor) {
        el.colorInput.value = pickerColor;
      }
    }

    if (annotation.type === 'rect') {
      const width = annotation.width ?? DEFAULT_RECT_SIZE.width;
      const height = annotation.height ?? DEFAULT_RECT_SIZE.height;
      root.style.width = `${width}px`;
      root.style.height = `${height}px`;
    } else {
      const width = annotation.width ?? DEFAULT_TEXT_WIDTH;
      root.style.width = `${width}px`;
      root.style.height = 'auto';
      if (el.content && !root.classList.contains('editing')) {
        el.content.textContent = annotation.text ?? '';
      }
    }
  };

  const updateElementPosition = (id: string, x: number, y: number) => {
    const el = elements.get(id);
    if (!el) return;
    el.root.style.left = `${x}px`;
    el.root.style.top = `${y}px`;
  };

  const updateElementSize = (
    id: string,
    width: number,
    height: number | null
  ) => {
    const el = elements.get(id);
    if (!el) return;
    el.root.style.width = `${width}px`;
    if (height !== null) {
      el.root.style.height = `${height}px`;
    }
  };

  const endDrag = () => {
    if (!dragState) return;
    const next = dragState;
    dragState = null;
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragEnd);
    window.removeEventListener('pointercancel', onDragEnd);
    const moved =
      next.nextX !== next.originX || next.nextY !== next.originY;
    const resized =
      next.mode === 'resize' &&
      (next.nextWidth !== next.originWidth ||
        (next.heightEnabled && next.nextHeight !== next.originHeight));
    if (!moved && !resized) {
      return;
    }
    const payload: Partial<Annotation> = {
      x: next.nextX,
      y: next.nextY,
    };
    if (next.mode === 'resize') {
      payload.width = Number.isFinite(next.nextWidth)
        ? next.nextWidth
        : undefined;
      if (next.heightEnabled) {
        payload.height = Number.isFinite(next.nextHeight)
          ? next.nextHeight
          : undefined;
      }
    }
    updateAnnotation(next.id, payload);
  };

  const onDragMove = (event: PointerEvent) => {
    if (!dragState) return;
    area.area.setPointerFrom(event);
    const { x, y } = area.area.pointer;
    if (dragState.mode === 'move') {
      const nextX = x - dragState.offsetX;
      const nextY = y - dragState.offsetY;
      dragState.nextX = nextX;
      dragState.nextY = nextY;
      updateElementPosition(dragState.id, nextX, nextY);
      return;
    }
    const dx = x - dragState.startX;
    const dy = y - dragState.startY;
    const nextWidth = Math.max(MIN_RECT_SIZE.width, dragState.startWidth + dx);
    dragState.nextWidth = nextWidth;
    if (dragState.heightEnabled) {
      const nextHeight = Math.max(
        MIN_RECT_SIZE.height,
        dragState.startHeight + dy
      );
      dragState.nextHeight = nextHeight;
      updateElementSize(dragState.id, nextWidth, nextHeight);
    } else {
      updateElementSize(dragState.id, nextWidth, null);
    }
  };

  const onDragEnd = () => {
    endDrag();
  };

  const startDrag = (
    event: PointerEvent,
    annotation: Annotation,
    mode: 'move' | 'resize'
  ) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    setSelection(annotation.id);
    area.area.setPointerFrom(event);
    const { x, y } = area.area.pointer;
    const width =
      annotation.width ??
      (annotation.type === 'rect' ? DEFAULT_RECT_SIZE.width : DEFAULT_TEXT_WIDTH);
    const height =
      annotation.height ??
      (annotation.type === 'rect' ? DEFAULT_RECT_SIZE.height : 0);
    dragState = {
      id: annotation.id,
      mode,
      offsetX: x - annotation.x,
      offsetY: y - annotation.y,
      startX: x,
      startY: y,
      originX: annotation.x,
      originY: annotation.y,
      originWidth: width,
      originHeight: height,
      startWidth: width,
      startHeight: height,
      nextX: annotation.x,
      nextY: annotation.y,
      nextWidth: width,
      nextHeight: height,
      heightEnabled: annotation.type === 'rect',
    };
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragEnd);
    window.addEventListener('pointercancel', onDragEnd);
  };

  const createAnnotationElement = (annotation: Annotation) => {
    const annotationId = annotation.id;
    const root = document.createElement('div');
    root.className = `annotation annotation-${annotation.type}`;
    root.dataset.annotationId = annotationId;

    let content: HTMLDivElement | undefined;
    if (annotation.type === 'text') {
      content = document.createElement('div');
      content.className = 'annotation-content';
      content.textContent = annotation.text ?? '';
      root.appendChild(content);
      content.addEventListener('dblclick', (event) => {
        event.stopPropagation();
        startEditing(annotationId);
      });
      content.addEventListener('pointerdown', (event) => {
        if (root.classList.contains('editing')) {
          event.stopPropagation();
        }
      });
    }

    const actions = document.createElement('div');
    actions.className = 'annotation-actions';
    const deleteButton = document.createElement('button');
    deleteButton.type = 'button';
    deleteButton.className = 'annotation-delete';
    deleteButton.textContent = '×';
    deleteButton.addEventListener('click', (event) => {
      event.stopPropagation();
      removeAnnotation(annotationId);
    });
    actions.appendChild(deleteButton);

    let colorInput: HTMLInputElement | undefined;
    if (annotation.type === 'rect') {
      colorInput = document.createElement('input');
      colorInput.type = 'color';
      colorInput.className = 'annotation-color';
      colorInput.ariaLabel = 'Rectangle color';
      colorInput.title = 'Rectangle color';
      colorInput.value =
        normalizeHexColor(annotation.color ?? colorFromId(annotation.id)) ??
        '#fbbf24';
      colorInput.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      });
      colorInput.addEventListener('change', (event) => {
        event.stopPropagation();
        updateAnnotation(annotationId, { color: colorInput?.value });
      });
      root.appendChild(colorInput);
    }
    root.appendChild(actions);

    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'annotation-resize';
    resizeHandle.addEventListener('pointerdown', (event) => {
      const current = getAnnotation(annotationId);
      if (!current) return;
      startDrag(event, current, 'resize');
    });
    root.appendChild(resizeHandle);

    root.addEventListener('pointerdown', (event) => {
      if (root.classList.contains('editing')) return;
      const target = event.target as HTMLElement;
      if (target.closest('.annotation-delete')) return;
      if (target.closest('.annotation-color')) return;
      if (target.closest('.annotation-resize')) return;
      if (event.button !== 0) {
        setSelection(annotationId);
        return;
      }
      const current = getAnnotation(annotationId);
      if (!current) return;
      startDrag(event, current, 'move');
    });

    root.addEventListener('dblclick', (event) => {
      if (annotation.type !== 'text') return;
      event.stopPropagation();
      startEditing(annotationId);
    });

    layer.appendChild(root);

    const entry = {
      root,
      content,
      deleteButton,
      colorInput,
      resizeHandle,
    };
    elements.set(annotationId, entry);
    applyAnnotationStyles(annotation, entry);
  };

  const sync = () => {
    const activeIds = new Set(annotations.map((item) => item.id));

    for (const annotation of annotations) {
      const existing = elements.get(annotation.id);
      if (!existing) {
        createAnnotationElement(annotation);
      } else {
        applyAnnotationStyles(annotation, existing);
      }
    }

    for (const [id, el] of elements.entries()) {
      if (!activeIds.has(id)) {
        layer.removeChild(el.root);
        elements.delete(id);
      }
    }

    if (pendingEditId && elements.has(pendingEditId)) {
      const id = pendingEditId;
      pendingEditId = null;
      requestAnimationFrame(() => startEditing(id));
    }
  };

  const addAnnotation = (annotation: Annotation, startEdit?: boolean) => {
    annotations = [...annotations, annotation];
    if (startEdit) {
      pendingEditId = annotation.id;
    }
    sync();
    emitChange();
  };

  const addTextAtPointer = () => {
    const annotation: Annotation = {
      id: createAnnotationId(),
      type: 'text',
      x: lastPointer.x,
      y: lastPointer.y,
      width: DEFAULT_TEXT_WIDTH,
      text: 'New note',
      color: pickAnnotationColor(),
    };
    addAnnotation(annotation, true);
  };

  const addRectAtPointer = () => {
    const annotation: Annotation = {
      id: createAnnotationId(),
      type: 'rect',
      x: lastPointer.x,
      y: lastPointer.y,
      width: DEFAULT_RECT_SIZE.width,
      height: DEFAULT_RECT_SIZE.height,
      color: pickAnnotationColor(),
    };
    addAnnotation(annotation, false);
  };

  const copySelectionToClipboard = async (): Promise<boolean> => {
    if (!selectedId) return false;
    const annotation = getAnnotation(selectedId);
    if (!annotation) return false;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify({
          kind: ANNOTATION_CLIPBOARD_KIND,
          annotation,
        })
      );
      return true;
    } catch (error) {
      console.error('Unable to copy annotation', error);
      return false;
    }
  };

  const cutSelectionToClipboard = async (): Promise<boolean> => {
    const didCopy = await copySelectionToClipboard();
    if (!didCopy || !selectedId) {
      return false;
    }
    removeAnnotation(selectedId);
    return true;
  };

  const pasteFromClipboardText = (text: string): boolean => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return false;
    }

    if (
      !parsed ||
      typeof parsed !== 'object' ||
      (parsed as { kind?: string }).kind !== ANNOTATION_CLIPBOARD_KIND
    ) {
      return false;
    }

    const source = (parsed as { annotation?: Annotation }).annotation;
    if (!source || (source.type !== 'text' && source.type !== 'rect')) {
      return false;
    }

    const annotation: Annotation = {
      id: createAnnotationId(),
      type: source.type,
      x: lastPointer.x,
      y: lastPointer.y,
      color: source.color,
    };

    if (source.type === 'rect') {
      annotation.width = Math.max(
        MIN_RECT_SIZE.width,
        source.width ?? DEFAULT_RECT_SIZE.width
      );
      annotation.height = Math.max(
        MIN_RECT_SIZE.height,
        source.height ?? DEFAULT_RECT_SIZE.height
      );
    } else {
      annotation.width = Math.max(
        MIN_RECT_SIZE.width,
        source.width ?? DEFAULT_TEXT_WIDTH
      );
      annotation.text = source.text ?? '';
    }

    addAnnotation(annotation, false);
    setSelection(annotation.id);
    return true;
  };

  const setAnnotations = (next: Annotation[]) => {
    annotations = Array.isArray(next) ? [...next] : [];
    sync();
  };

  const getAnnotations = () => [...annotations];

  container.addEventListener('pointermove', updatePointer);
  container.addEventListener('pointerdown', updatePointer);
  container.addEventListener('contextmenu', handleContextMenu);
  container.addEventListener('pointerdown', handleBackgroundPointerDown, true);

  return {
    setAnnotations,
    getAnnotations,
    addTextAtPointer,
    addRectAtPointer,
    hasSelection: () => selectedId !== null,
    copySelectionToClipboard,
    cutSelectionToClipboard,
    pasteFromClipboardText,
    destroy: () => {
      window.removeEventListener('pointermove', onDragMove);
      window.removeEventListener('pointerup', onDragEnd);
      window.removeEventListener('pointercancel', onDragEnd);
      container.removeEventListener('pointermove', updatePointer);
      container.removeEventListener('pointerdown', updatePointer);
      container.removeEventListener('contextmenu', handleContextMenu);
      container.removeEventListener(
        'pointerdown',
        handleBackgroundPointerDown,
        true
      );
      area.area.content.remove(layer);
      elements.clear();
    },
  };
}
